// Comprehensive regulations data for the dynamic article system
import type {
    RegulationsDataType,
    RegulationsNavigationType,
    FeaturedRegulationsType,
    RegulationContentBlock,
} from "../types/regulations";

export const regulationsData: RegulationsDataType = {
    "eu-ecodesign-external-power-supplies": {
        id: "eu-ecodesign-external-power-supplies",
        title: "Commission Regulation (EU) 2019/1782 - Ecodesign Requirements for External Power Supplies",
        shortTitle: "EU Ecodesign for External Power Supplies",
        regulationType: "REGULATORY BRIEF",
        priority: "URGENT",
        category: "EU Regulation",
        tags: ["Ecodesign", "External Power Supplies"],
        filterTags: ["Ecodesign", "Energy Efficiency"],
        miscTags: ["Computer", "Smart Home"],
        regionTags: ["Europe"],
        publishDate: "2019-10-01",
        effectiveDate: "2020-04-01",
        lastUpdated: "2024-01-15",
        regions: ["EU", "EEA", "Northern Ireland"],
        author: {
            name: "Regulatory Team",
            email: "briefs@example.com",
            photoUrl: "/img/PlaceholderImgs/face1.jpg",
        },
        headerQuote:
            "Structured overview and initial assessment of ESPR scope, key requirements and next steps.",
        executiveSummary:
            "On 30 March 2022, the European Commission proposed the Ecodesign for Sustainable Products Regulation (ESPR), extending the existing Ecodesign Directive to the broadest possible range of products. Key novelties include an EU digital product passport and provisions to address substances of concern, intersecting with REACH and WFD/SCIP. This brief provides a structured overview, highlights key new requirements, and outlines next steps.",
        importantDates: [
            {
                label: "Publish date",
                date: "2019-10-25",
                notes: "published in the Official Journal",
            },
            {
                label: "Enforcement date",
                date: "2020-04-01",
                notes: "3 year implementation period",
            },
            {
                label: "Service parts exemption expiration date",
                date: "2025-04-01",
                notes: "service parts can be sold under specific conditions after the main enforcement date",
            },
        ],
        sections: [
            {
                title: "Full Official Name of the Regulation",
                content:
                    "Commission Regulation (EU) 2019/1782 of 1 October 2019 laying down ecodesign requirements for external power supplies pursuant to Directive 2009/125/EC of the European Parliament and of the Council and repealing Commission Regulation (EC) No 278/2009.",
            },
            {
                title: "Scope of Impacted Products",
                content:
                    "The regulation applies to external power supplies (EPS), defined as devices that convert household AC power into lower voltage DC or AC output, are sold with or marketed for a separate electrical and electronic product, have a single AC or DC output voltage, and have a nameplate output power of ≤250 Watts. This includes the power adapters and chargers for a vast range of consumer electronics like laptops, smartphones, tablets, routers, speakers, and home office equipment.",
            },
            {
                title: "Exemptions",
                contentBlocks: [
                    {
                        type: "paragraph",
                        runs: [
                            {
                                text: "The following are explicitly exempt from this regulation:",
                            },
                        ],
                    },
                    {
                        type: "ul",
                        items: [
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [{ text: "Voltage converters" }],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Uninterruptible power supplies (UPS)",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Battery chargers without a power supply function for another appliance",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Lighting converters (e.g., LED drivers)",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "External power supplies for medical devices",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Docking stations for autonomous appliances",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Active Power over Ethernet (PoE) injectors",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "External power supplies placed on the market before April 1, 2025, as service parts for identical EPS placed on the market before April 1, 2020",
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: "Regions Impacted",
                content:
                    "Primary: The 27 member states of the European Union (EU). Directly Leveraged: The regulation applies in the European Economic Area (EEA), including Iceland, Liechtenstein, and Norway, and to Northern Ireland under the Windsor Framework. Indirect Impact: The EU's stringent efficiency standards often become a de facto global standard. Companies in the US, Asia, and other regions often design their products to meet these EU requirements, effectively harmonizing their entire product line to this higher standard.",
            },
            {
                title: "Design Changes Due to Regulation",
                contentBlocks: [
                    {
                        type: "paragraph",
                        runs: [
                            {
                                text: "The regulation requires several specific design improvements:",
                            },
                        ],
                    },
                    {
                        type: "ol",
                        items: [
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Increased Active Mode Efficiency:",
                                                bold: true,
                                            },
                                            {
                                                text: " Designs must meet higher average efficiency limits across four load points (10%, 25%, 50%, and 100%).",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Reduced No-Load Power Consumption:",
                                                bold: true,
                                            },
                                            {
                                                text: " Maximum power consumption in the no-load condition is significantly reduced.",
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "10% Load Efficiency Requirement:",
                                                bold: true,
                                            },
                                            {
                                                text: " A new minimum efficiency level is mandated at 10% load, forcing optimization for low-power states.",
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: "Service Requirements",
                content:
                    "In line with the EU's circular economy goals, manufacturers must make spare parts (specifically, the external casing and internal capacitors) available to professional repairers for a minimum of 7 years after the last unit of a model has been placed on the market. Access to repair and maintenance information must also be provided to professional repairers.",
            },
            {
                title: "Product Markings",
                content:
                    "The following information must be permanently and legibly marked on the nameplate of the external power supply: Manufacturer's name, registered trade name, or trade mark. Manufacturer's registered commercial registration number and address. Model identifier. Input voltage (V), input AC frequency (Hz). Output voltage (V), output current (A), and output power (W). Average active efficiency (as a percentage). Efficiency at low load (10%) (as a percentage). No-load power consumption (W).",
            },
            {
                title: "List of Packaging Markings",
                content:
                    "There are no new marking requirements specific to the retail packaging itself. However, the required product and user guide information effectively influences packaging content decisions.",
            },
            {
                title: "List of Software or Performance Criteria",
                content:
                    "The core performance criteria are the electrical performance standards detailed in Section 7 (Design Changes). There are no software-specific requirements within this regulation.",
            },
            {
                title: "List of Test, Certification, and Registration Requirements",
                content:
                    "Conformity Assessment: Manufacturers must perform a conformity assessment and draw up a Declaration of Conformity (DoC), declaring the product complies with the regulation. CE Marking: The CE mark must be affixed to the product, signifying compliance with all applicable EU directives and regulations. Technical Documentation File: Manufacturers must create and maintain a Technical Documentation File. This file must contain the test reports and design data proving compliance with all aspects of the regulation. It must be made available to market surveillance authorities upon request for up to 10 years after the last product has been placed on the market. EPREL Database Registration: Before placing an EPS model on the market, the manufacturer or importer must register specific information about the model in the European Product Registry for Energy Labelling (EPREL). The public part of this database provides transparency for consumers, while the compliance part is used by market surveillance authorities.",
            },
            {
                title: "Summary of Major Compliance Challenges",
                content:
                    "Meeting New Efficiency Tiers: The stricter efficiency requirements, especially the new 10% load condition, may require significant redesign of power supply circuits, potentially increasing component costs and size. Information & Marking Overload: The volume of technical information now required directly on the small nameplate of an EPS presents a significant physical design challenge. This often requires fine-pitch laser etching or printing. Administrative Burden of EPREL: The requirement to register every EPS model in the EPREL database before market placement adds a critical administrative step to the product launch process. Failure to register on time will delay market access. Managing Service & Repair Obligations: Establishing a process to provide spare parts and repair information to professional repairers for 7 years post-production is a new logistical and legal challenge for many electronics companies.",
            },
            {
                title: "Referenced Materials",
                content:
                    "Regulator's Information Page: European Commission - Ecodesign for External Power Supplies. Official Regulation Text: Commission Regulation (EU) 2019/1782 on EUR-Lex. EPREL Database: European Product Registry for Energy Labelling.",
            },
            {
                title: "How We Can Help",
                content:
                    "Navigating the complexities of Regulation (EU) 2019/1782 requires expertise in both engineering and regulatory compliance. The consequences of failure are too high to leave to chance. Our consulting services provide end-to-end support to ensure your products are fully compliant and ready for the EU market. We offer: Technical Documentation Review and Gap Analysis: We assess your existing designs and documentation against the regulation's requirements. EPREL Registration Support: We manage the entire EPREL submission process, from data collection to final registration, ensuring timely market access. Testing and Certification Strategy: We guide you on the most efficient path to generating the required test data and creating a robust Declaration of Conformity. Compliance Process Integration: We help you build the internal processes needed to manage long-term obligations, such as service and repair information management. Contact us today to schedule a consultation and secure your company's access to the EU market.",
            },
        ],
    },
    "gdpr-data-protection": {
        id: "gdpr-data-protection",
        title: "General Data Protection Regulation (GDPR) - EU 2016/679",
        shortTitle: "GDPR Data Protection",
        regulationType: "REGULATORY BRIEF",
        priority: "HIGH",
        category: "Privacy Law",
        tags: ["GDPR", "Data Protection", "Privacy", "EU Regulation"],
        filterTags: ["Standards"],
        miscTags: ["Computer", "Smartphone"],
        regionTags: ["Europe", "Global"],
        publishDate: "2016-04-27",
        effectiveDate: "2018-05-25",
        lastUpdated: "2024-02-20",
        regions: ["EU", "EEA", "Global"],
        author: {
            name: "Privacy Team",
            email: "gdpr@example.com",
            photoUrl: "/img/PlaceholderImgs/face2.jpg",
        },
        headerQuote: "Overview of GDPR scope, key principles, and obligations.",
        executiveSummary:
            "GDPR sets comprehensive rules for personal data processing, with extraterritorial reach and a strong rights framework. It defines lawful bases, requires DPIAs in high-risk cases, mandates breach notifications, and authorizes substantial fines for non-compliance.",
        importantDates: [
            {
                label: "Publish date",
                date: "2016-05-04",
                notes: "published in the Official Journal",
            },
            { label: "Enforcement date", date: "2018-05-25" },
        ],
        sections: [
            {
                title: "Full Official Name of the Regulation",
                content:
                    "Regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016 on the protection of natural persons with regard to the processing of personal data and on the free movement of such data, and repealing Directive 95/46/EC (General Data Protection Regulation).",
            },
            {
                title: "Scope of Application",
                content:
                    "GDPR applies to the processing of personal data of individuals who are in the EU by a controller or processor, regardless of whether the processing takes place in the EU or not. It also applies to the processing of personal data of individuals in the EU by a controller or processor not established in the EU, where the processing activities are related to offering goods or services to such individuals, or monitoring their behavior.",
            },
            {
                title: "Key Principles",
                contentBlocks: [
                    {
                        type: "paragraph",
                        runs: [
                            { text: "GDPR is built on seven key principles:" },
                        ],
                    },
                    {
                        type: "ol",
                        items: [
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Lawfulness, fairness and transparency:",
                                                bold: true,
                                            },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Processing must be lawful, fair and transparent",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Purpose limitation:",
                                                bold: true,
                                            },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Data must be collected for specified, explicit and legitimate purposes",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Data minimisation:",
                                                bold: true,
                                            },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Personal data must be adequate, relevant and limited to what is necessary",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            { text: "Accuracy:", bold: true },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Personal data must be accurate and kept up to date",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Storage limitation:",
                                                bold: true,
                                            },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Data must be kept in a form that permits identification for no longer than necessary",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Integrity and confidentiality:",
                                                bold: true,
                                            },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Appropriate security measures must be implemented",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                blocks: [
                                    {
                                        type: "paragraph",
                                        runs: [
                                            {
                                                text: "Accountability:",
                                                bold: true,
                                            },
                                        ],
                                    },
                                    {
                                        type: "ul",
                                        items: [
                                            {
                                                blocks: [
                                                    {
                                                        type: "paragraph",
                                                        runs: [
                                                            {
                                                                text: "Controllers must be able to demonstrate compliance",
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: "Individual Rights",
                content: `
          <p>Under GDPR, individuals have the following rights:</p>
          <ol>
            <li><strong>Right to be informed</strong> - Individuals have the right to know how their data is being processed</li>
            <li><strong>Right of access</strong> - Individuals can request access to their personal data</li>
            <li><strong>Right to rectification</strong> - Individuals can request correction of inaccurate data</li>
            <li><strong>Right to erasure</strong> (right to be forgotten) - Individuals can request deletion of their data</li>
            <li><strong>Right to restrict processing</strong> - Individuals can limit how their data is processed</li>
            <li><strong>Right to data portability</strong> - Individuals can request their data in a portable format</li>
            <li><strong>Right to object</strong> - Individuals can object to certain types of processing</li>
            <li><strong>Rights in relation to automated decision making and profiling</strong> - Protection against automated decision-making</li>
          </ol>
        `,
            },
            {
                title: "Legal Basis for Processing",
                content:
                    "Consent: The individual has given clear consent for you to process their personal data for a specific purpose. Contract: The processing is necessary for a contract you have with the individual, or because they have asked you to take specific steps before entering into a contract. Legal obligation: The processing is necessary for you to comply with the law. Vital interests: The processing is necessary to protect someone's life. Public task: The processing is necessary for you to perform a task in the public interest or for your official functions. Legitimate interests: The processing is necessary for your legitimate interests or the legitimate interests of a third party.",
            },
            {
                title: "Data Protection Impact Assessments (DPIA)",
                content:
                    "A DPIA is required when processing is likely to result in a high risk to individuals' rights and freedoms, particularly for systematic and extensive evaluation, processing of special categories of data, or systematic monitoring of public areas. The assessment must describe the processing, assess necessity and proportionality, identify and assess risks, and identify measures to address risks.",
            },
            {
                title: "Breach Notification Requirements",
                content:
                    "Controllers must notify the relevant supervisory authority of a personal data breach within 72 hours of becoming aware of it, unless the breach is unlikely to result in a risk to rights and freedoms. If the breach is likely to result in a high risk to rights and freedoms, individuals must also be notified without undue delay.",
            },
            {
                title: "Data Protection Officer (DPO) Requirements",
                content:
                    "A DPO must be appointed where processing is carried out by a public authority, core activities consist of regular and systematic monitoring of individuals on a large scale, or core activities consist of processing special categories of data or criminal conviction data on a large scale.",
            },
            {
                title: "International Data Transfers",
                content:
                    "Transfers of personal data to third countries or international organizations may only take place if the third country ensures an adequate level of protection, appropriate safeguards are provided, or specific derogations for specific situations apply. Standard Contractual Clauses (SCCs) and Binding Corporate Rules (BCRs) are common mechanisms for ensuring adequate protection.",
            },
            {
                title: "Penalties and Fines",
                content:
                    "Administrative fines up to €10 million or 2% of annual global turnover (whichever is higher) for certain infringements. Administrative fines up to €20 million or 4% of annual global turnover (whichever is higher) for more serious infringements, including violations of basic principles, individual rights, and international transfer provisions.",
            },
            {
                title: "Compliance Challenges",
                content:
                    "Understanding complex legal requirements, implementing privacy by design and by default, managing consent mechanisms, conducting DPIAs, establishing procedures for individual rights requests, ensuring adequate security measures, managing international data transfers, and maintaining comprehensive documentation of processing activities.",
            },
            {
                title: "How We Can Help",
                content:
                    "Our GDPR compliance services provide comprehensive support to ensure your organization meets all regulatory requirements. We offer: GDPR compliance audits and gap analyses, Privacy policy and notice development, Data mapping and processing inventory creation, DPIA facilitation and documentation, Privacy by design implementation guidance, Staff training and awareness programs, Incident response planning and breach notification procedures, International data transfer mechanism setup.",
            },
        ],
    },
    "fcc-equipment-authorization": {
        id: "fcc-equipment-authorization",
        title: "FCC Equipment Authorization Requirements - Part 15",
        shortTitle: "FCC Equipment Authorization",
        regulationType: "REGULATORY BRIEF",
        category: "US Federal Regulation",
        tags: [
            "FCC",
            "Equipment Authorization",
            "Radio Frequency",
            "US Regulation",
        ],
        filterTags: ["RF", "EMC", "Safety"],
        miscTags: ["Smartphone", "Drone", "Computer"],
        regionTags: ["North America"],
        publishDate: "1985-01-01",
        effectiveDate: "1985-01-01",
        lastUpdated: "2024-03-10",
        regions: ["United States", "US Territories"],
        author: { name: "RF Compliance", email: "fcc@example.com" },
        importantDates: [
            { label: "Original effective date", date: "1985-01-01" },
        ],
        sections: [
            {
                title: "Full Official Name of the Regulation",
                content:
                    "Federal Communications Commission Rules and Regulations Part 15 - Radio Frequency Devices, specifically covering Equipment Authorization requirements for devices that emit radio frequency energy.",
            },
            {
                title: "Short or Common Name",
                content:
                    "FCC Part 15, FCC Equipment Authorization, FCC Certification.",
            },
            {
                title: "Scope of Impacted Products",
                content:
                    "Part 15 applies to radio frequency devices, including but not limited to: intentional radiators (Wi-Fi devices, Bluetooth devices, remote controls), unintentional radiators (computers, monitors, switching power supplies), and incidental radiators (devices that generate RF energy for internal use). This covers a vast range of consumer electronics, industrial equipment, and telecommunications devices.",
            },
            {
                title: "Equipment Authorization Procedures",
                content:
                    "Supplier's Declaration of Conformity (SDoC): For devices with lower interference potential. Certification: Required for devices with higher interference potential, involving testing by accredited laboratories and review by Telecommunications Certification Bodies (TCBs). The specific procedure depends on the device type and power levels.",
            },
            {
                title: "Technical Standards and Testing",
                content:
                    "Devices must comply with specific emission limits and technical requirements outlined in Part 15. Testing must be performed by accredited test laboratories using approved test methods. Common test standards include conducted emissions, radiated emissions, bandwidth measurements, and spurious emissions testing.",
            },
            {
                title: "Labeling Requirements",
                content:
                    "FCC ID: Required for certified devices, must be displayed on the device label. Supplier's Declaration statement: Required for SDoC devices. User manual requirements: Must include FCC compliance statements and operational restrictions. The FCC ID must be permanently affixed to the device or electronically displayed.",
            },
            {
                title: "Marketing and Import Requirements",
                content:
                    "Devices must receive equipment authorization before marketing or importation into the United States. The responsible party (manufacturer, importer, or authorized agent) must ensure compliance and maintain required documentation. Marketing includes advertising, sale, lease, or offer for sale.",
            },
            {
                title: "Responsible Party Obligations",
                content:
                    "Maintain test reports and technical documentation. Ensure continued compliance during production. Implement quality assurance procedures. Respond to FCC inquiries and enforcement actions. File required reports and notifications with the FCC when applicable.",
            },
            {
                title: "Changes and Modifications",
                content:
                    "Permissive changes: Minor changes that don't affect emissions characteristics may not require re-authorization. Class I changes: Require new measurements but no new authorization. Class II changes: Require new equipment authorization. Class III changes: Require complete re-authorization.",
            },
            {
                title: "Compliance Testing Requirements",
                content:
                    "Testing must be performed according to specific measurement procedures outlined in Part 15 and referenced industry standards. Key requirements include: conducted emissions testing (150 kHz to 30 MHz), radiated emissions testing (30 MHz to 1 GHz and beyond for some devices), bandwidth and spurious emissions measurements for intentional radiators.",
            },
            {
                title: "Enforcement and Penalties",
                content:
                    "The FCC can impose monetary forfeitures, equipment seizure, cease and desist orders, and criminal penalties for willful violations. Penalties can range from thousands to millions of dollars depending on the violation severity and company size.",
            },
            {
                title: "Common Compliance Challenges",
                content:
                    "Understanding which authorization procedure applies to specific products. Managing the complexity of combination devices with multiple radio technologies. Ensuring proper labeling and user manual content. Maintaining compliance during product modifications and production variations. Coordinating with accredited test laboratories and TCBs for efficient processing.",
            },
            {
                title: "How We Can Help",
                content:
                    "Our FCC compliance services provide complete support for equipment authorization requirements. We offer: Pre-compliance testing and design guidance to identify issues early. Full compliance testing coordination with accredited laboratories. Equipment authorization application preparation and submission. TCB liaison and application tracking services. Labeling and user manual review and development. Post-authorization compliance monitoring and change management. Import and customs coordination support.",
            },
        ],
    },
    "rohs-restriction-hazardous-substances": {
        id: "rohs-restriction-hazardous-substances",
        title: "EU RoHS Directive 2011/65/EU - Restriction of Hazardous Substances",
        shortTitle: "EU RoHS Directive",
        category: "Environmental Regulation",
        tags: ["RoHS", "Hazardous Substances", "EU Directive", "Environmental"],
        filterTags: ["Restricted Substances", "Ecodesign"],
        miscTags: ["Smartphone", "Computer", "Battery"],
        regionTags: ["Europe"],
        publishDate: "2011-06-08",
        effectiveDate: "2013-01-03",
        lastUpdated: "2024-01-20",
        regions: ["EU", "EEA", "Northern Ireland"],
        importantDates: [
            { label: "Publication", date: "2011-06-08" },
            { label: "Transposition deadline", date: "2013-01-02" },
            { label: "Application to all EEE", date: "2019-07-22" },
        ],
        sections: [
            {
                title: "Full Official Name of the Regulation",
                content:
                    "Directive 2011/65/EU of the European Parliament and of the Council of 8 June 2011 on the restriction of the use of certain hazardous substances in electrical and electronic equipment (recast).",
            },
            {
                title: "Short or Common Name",
                content:
                    "RoHS Directive, RoHS 2, EU RoHS, Restriction of Hazardous Substances Directive.",
            },
            {
                title: "Scope of Application",
                content:
                    "RoHS applies to electrical and electronic equipment (EEE) placed on the EU market, including equipment in the following categories: Large household appliances, Small household appliances, IT and telecommunications equipment, Consumer equipment, Lighting equipment, Electrical and electronic tools, Toys/leisure/sports equipment, Medical devices, Monitoring and control instruments, Automatic dispensers.",
            },
            {
                title: "Restricted Substances",
                content:
                    "The directive restricts the use of 10 hazardous substances: Lead (Pb) - maximum concentration 0.1% by weight in homogeneous materials. Mercury (Hg) - maximum concentration 0.1% by weight. Cadmium (Cd) - maximum concentration 0.01% by weight. Hexavalent chromium (Cr6+) - maximum concentration 0.1% by weight. Polybrominated biphenyls (PBB) - maximum concentration 0.1% by weight. Polybrominated diphenyl ethers (PBDE) - maximum concentration 0.1% by weight. Plus four additional substances added in 2019: Bis(2-ethylhexyl) phthalate (DEHP), Benzyl butyl phthalate (BBP), Dibutyl phthalate (DBP), Diisobutyl phthalate (DIBP) - all with maximum concentration 0.1% by weight.",
            },
            {
                title: "Exemptions",
                content:
                    "Specific exemptions are listed in Annexes III and IV of the directive, including: Applications where substitution is scientifically or technically impracticable, Applications where negative environmental, health, or consumer safety impacts would likely outweigh benefits, Lead in high melting temperature solders, Mercury in certain types of fluorescent lamps, Cadmium in certain photoresistors. Exemptions are regularly reviewed and may be renewed, amended, or revoked.",
            },
            {
                title: "CE Marking and Declaration of Conformity",
                content:
                    "Manufacturers must affix the CE marking to compliant products and draw up an EU declaration of conformity. The declaration must include: Product identification, Name and address of manufacturer, Statement of compliance with RoHS, Reference to harmonized standards used, Date and place of declaration, Name and signature of authorized person.",
            },
            {
                title: "Technical Documentation Requirements",
                content:
                    "Manufacturers must compile technical documentation demonstrating compliance, including: General description of the EEE, Risk assessment and analysis of chemical requirements, Compliance test reports, Evidence of conformity with exemptions (if applicable), Copies of relevant technical standards applied, Technical documentation must be kept for 10 years after the product is placed on the market.",
            },
            {
                title: "Supply Chain Management",
                content:
                    "Effective RoHS compliance requires comprehensive supply chain management: Supplier qualification and auditing, Material declarations and certificates of compliance, Incoming inspection and testing procedures, Component and material traceability systems, Change control procedures for material substitutions.",
            },
            {
                title: "Testing and Verification",
                content:
                    "Common analytical methods for RoHS compliance verification include: X-ray fluorescence (XRF) spectroscopy for screening, Inductively coupled plasma (ICP) methods for confirmation, Gas chromatography-mass spectrometry (GC-MS) for organic substances, Wet chemical analysis methods as reference standards.",
            },
            {
                title: "Market Surveillance and Enforcement",
                content:
                    "Member state authorities conduct market surveillance activities including: Product testing and inspection, Documentation reviews, Factory audits and assessments, Non-compliant products may be subject to: Market withdrawal or recall, Import restrictions, Administrative penalties, Criminal prosecution for serious violations.",
            },
            {
                title: "Compliance Challenges",
                content:
                    "Complex global supply chains with multiple tiers of suppliers, Analytical testing limitations and measurement uncertainties, Managing exemption applications and renewals, Coordinating with other regulations (REACH, WEEE, etc.), Keeping up with evolving substance restrictions and exemption changes, Ensuring consistent interpretation across different member states.",
            },
            {
                title: "How We Can Help",
                content:
                    "Our RoHS compliance services provide comprehensive support for substance restriction requirements. We offer: Supply chain assessment and supplier qualification programs, Material analysis and testing coordination, Technical documentation development and review, Exemption application preparation and management, CE marking and declaration of conformity support, Compliance training and awareness programs, Market surveillance response and remediation support.",
            },
        ],
    },
    "california-energy-commission-appliance-efficiency": {
        id: "california-energy-commission-appliance-efficiency",
        title: "California Appliance Efficiency Regulations - Title 20",
        shortTitle: "California Appliance Efficiency",
        regulationType: "REGULATORY BRIEF",
        category: "State Energy Regulation",
        tags: ["California", "Energy Efficiency", "CEC", "Title 20"],
        filterTags: ["Energy Efficiency", "Standards"],
        miscTags: ["Smart Home", "Computer"],
        regionTags: ["North America"],
        publishDate: "1976-01-01",
        effectiveDate: "1976-01-01",
        lastUpdated: "2024-02-15",
        regions: ["California", "US (market influence)"],
        author: { name: "Energy Standards", email: "cec@example.com" },
        importantDates: [{ label: "Original adoption", date: "1976-01-01" }],
        sections: [
            {
                title: "Full Official Name of the Regulation",
                content:
                    "California Code of Regulations, Title 20, Division 2, Chapter 4 - Appliance Efficiency Regulations, administered by the California Energy Commission (CEC).",
            },
            {
                title: "Short or Common Name",
                content:
                    "California Title 20, CEC Appliance Efficiency Regulations, California Energy Standards.",
            },
            {
                title: "Scope of Regulated Products",
                content:
                    "Title 20 covers a wide range of appliances and equipment sold or offered for sale in California, including: External power supplies, Battery charger systems, Computers and computer monitors, Television sets, Set-top boxes, Small motors, Pool/spa equipment, Portable air conditioners, Commercial and residential refrigeration, Water heaters, Lighting products.",
            },
            {
                title: "Registration and Certification Requirements",
                content:
                    "Manufacturers must register covered products with the CEC's Modernized Appliance Efficiency Database System (MAEDbS) before offering them for sale in California. Registration requires: Product specifications and efficiency data, Test report from CEC-approved laboratory, Certification that the product meets applicable standards, Annual registration fees, Updated information when product specifications change.",
            },
            {
                title: "Testing and Certification Standards",
                content:
                    "Products must be tested by laboratories recognized by the CEC using specific test methods: External power supplies: CEC test method or DOE 10 CFR 430 methods, Battery chargers: IEEE 1725 or CEC-specific test procedures, Computers: ENERGY STAR test methods, Television sets: CEC test methods based on IEC standards, All testing must be performed by laboratories with appropriate accreditation and CEC recognition.",
            },
            {
                title: "Efficiency Standards and Requirements",
                content:
                    "Each product category has specific efficiency standards and operational requirements. For example: External power supplies must meet active mode efficiency levels at multiple load points, Battery chargers have standby power limitations and charging efficiency requirements, Computers must meet energy consumption limits in different operational modes, Television sets have maximum power consumption limits based on screen area and resolution.",
            },
            {
                title: "Marking and Documentation Requirements",
                content:
                    "Covered products must display required markings and include specific information: Model number and efficiency ratings must be permanently marked on the product, User manuals must include energy consumption information and operational guidance, Marketing materials must not contain false or misleading efficiency claims, Some products require energy guide labels or efficiency information displays.",
            },
            {
                title: "Compliance Verification and Enforcement",
                content:
                    "The CEC conducts compliance verification through: Market surveillance testing of registered products, Review of manufacturer test reports and data, Facility inspections and audits, Investigation of consumer complaints and reports, Non-compliance may result in: Administrative penalties up to $2,500 per violation per day, Product sale prohibition in California, Required corrective actions and recalls.",
            },
            {
                title: "Alternative Compliance Pathways",
                content:
                    "For certain product categories, manufacturers may use alternative compliance approaches: Federal standards equivalency (if CEC determines federal standards are equivalent or more stringent), Utility incentive programs (for specific product categories), Early compliance credits (for products exceeding minimum requirements), Custom efficiency standards (for unique product designs with CEC approval).",
            },
            {
                title: "Relationship to Federal Standards",
                content:
                    "California standards often exceed federal requirements and may serve as precursors to national standards. Key considerations include: Preemption analysis for products subject to both state and federal standards, Coordination with ENERGY STAR and DOE efficiency programs, Market influence extending beyond California due to manufacturer standardization, Potential adoption of California standards by other states.",
            },
            {
                title: "Compliance Challenges",
                content:
                    "Navigating complex registration and database requirements, Understanding product-specific test methods and efficiency calculations, Managing multiple product variants and model registrations, Coordinating with other California environmental regulations (Prop 65, RoHS, etc.), Keeping current with frequently updated standards and requirements, Ensuring timely registration before product launch deadlines.",
            },
            {
                title: "How We Can Help",
                content:
                    "Our California Title 20 compliance services provide complete regulatory support. We offer: Product applicability assessments and regulatory pathway analysis, Testing coordination with CEC-approved laboratories, MAEDbS registration and database management, Compliance documentation development and review, Market surveillance response and remediation, Regulatory update monitoring and impact analysis, Multi-state efficiency standard coordination and planning.",
            },
        ],
    },
};

// Navigation structure for IndexSideNav
export const regulationsNavigation: RegulationsNavigationType = [
    {
        title: "Energy Efficiency",
        items: [
            {
                title: "EU Ecodesign for External Power Supplies",
                id: "eu-ecodesign-external-power-supplies",
                description:
                    "EU efficiency requirements for external power supplies",
            },
            {
                title: "California Appliance Efficiency",
                id: "california-energy-commission-appliance-efficiency",
                description: "California Title 20 energy efficiency standards",
            },
        ],
    },
    {
        title: "Data Protection & Privacy",
        items: [
            {
                title: "GDPR Data Protection",
                id: "gdpr-data-protection",
                description:
                    "EU General Data Protection Regulation requirements",
            },
        ],
    },
    {
        title: "Radio Frequency & EMC",
        items: [
            {
                title: "FCC Equipment Authorization",
                id: "fcc-equipment-authorization",
                description:
                    "US FCC Part 15 equipment authorization requirements",
            },
        ],
    },
    {
        title: "Environmental Compliance",
        items: [
            {
                title: "EU RoHS Directive",
                id: "rohs-restriction-hazardous-substances",
                description:
                    "EU restriction of hazardous substances in electronics",
            },
        ],
    },
];

// Featured regulations for the main page
export const featuredRegulations: FeaturedRegulationsType = [
    {
        id: "1",
        image: "/img/examples/card-blog1.jpg",
        header: "EU Ecodesign for External Power Supplies",
        subheader: "ENERGY EFFICIENCY",
        description:
            "Commission Regulation (EU) 2019/1782 laying down ecodesign requirements for external power supplies with detailed compliance guidance.",
        ctaText: "View Details",
        date: "2024-01-15",
        category: "Energy",
        link: "/regulations/eu-ecodesign-external-power-supplies",
    },
    {
        id: "2",
        image: "/img/examples/card-blog2.jpg",
        header: "GDPR Data Protection",
        subheader: "PRIVACY",
        description:
            "Comprehensive guide to EU General Data Protection Regulation (GDPR) compliance requirements and individual rights.",
        ctaText: "Learn More",
        date: "2024-02-20",
        category: "Privacy",
        link: "/regulations/gdpr-data-protection",
    },
    {
        id: "3",
        image: "/img/examples/card-blog3.jpg",
        header: "FCC Equipment Authorization",
        subheader: "TELECOMMUNICATIONS",
        description:
            "FCC Part 15 equipment authorization requirements and certification procedures for electronic devices.",
        ctaText: "Explore",
        date: "2024-03-10",
        category: "Telecom",
        link: "/regulations/fcc-equipment-authorization",
    },
];
